/**
 * Popover with chip-style toggles for each available column.
 * Mirrors the visual language of TraceLogPanel's context controls
 * (Same Session / Same Endpoint / …) so the dashboard reads as one
 * design system: active = green border + green-soft bg, inactive =
 * line border + muted text.
 *
 * Pairs with `useColumnSelection`:
 *   const sel = useColumnSelection({ storageKey, columns, defaultVisible });
 *   <ColumnPicker
 *     columns={ALL_COLUMNS}
 *     visibleIds={sel.visibleIds}
 *     onToggle={sel.toggle}
 *     onSetVisible={sel.setVisible}
 *     onResetToDefault={sel.resetToDefault}
 *     isOverridden={sel.isOverridden}
 *   />
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ACTION_TEXT_CLASS, NOTE_CLASS } from "../lib/ui.js";

// Trigger chrome per host surface. "header" is the original
// AdminViewHeader treatment (38px, open bottom edge fusing into the
// header rule). "input" matches a search <input> so the picker can sit
// beside one in a form row (the Inventory lens).
const TRIGGER_VARIANTS = {
    header: "relative flex min-h-[38px] shrink-0 items-stretch border border-b-0 border-[color:var(--line)] bg-[color:var(--control-bg)]",
    input: "relative flex min-h-[30px] shrink-0 items-stretch rounded-[2px] border border-[color:var(--line)] bg-transparent hover:border-[color:var(--line-strong)]",
};

export default function ColumnPicker({
    columns,
    visibleIds,
    onToggle,
    onSetVisible,
    onResetToDefault,
    isOverridden = false,
    label = "Columns",
    variant = "header",
}) {
    const [open, setOpen] = useState(false);
    const btnRef = useRef(null);
    const popRef = useRef(null);
    const [pos, setPos] = useState({ top: 0, right: 0 });

    useLayoutEffect(() => {
        if (!open || !btnRef.current) return;
        const place = () => {
            const r = btnRef.current.getBoundingClientRect();
            setPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
        };
        place();
        window.addEventListener("resize", place);
        window.addEventListener("scroll", place, true);
        return () => {
            window.removeEventListener("resize", place);
            window.removeEventListener("scroll", place, true);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (popRef.current?.contains(e.target)) return;
            if (btnRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const visibleCount = columns.reduce((n, c) => n + (visibleIds.has(c.id) ? 1 : 0), 0);
    const allShown = visibleCount === columns.length;
    // Worst-case count text is "(N/N)" where both numbers have the
    // same digit count — `tabular-nums` + a min-width keep the
    // trigger button at a stable width regardless of how many columns
    // are toggled, so the popover's right-edge anchor doesn't shift
    // out from under itself.
    const countMinCh = `${columns.length}/${columns.length}`.length + 2;

    // Replace the full set in one go — calling `onToggle` in a loop
    // doesn't compose because each invocation reads the closure-
    // captured set independently and only the last write wins.
    const handleShowAll = useCallback(() => {
        if (!onSetVisible) return;
        if (allShown) return;
        onSetVisible(new Set(columns.map((c) => c.id)));
    }, [onSetVisible, allShown, columns]);

    return (
        <div className={TRIGGER_VARIANTS[variant] || TRIGGER_VARIANTS.header}>
            <button
                ref={btnRef}
                type="button"
                onClick={() => setOpen((v) => !v)}
                title="Show / hide columns"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label="Show or hide columns"
                className="inline-flex items-center px-[10px] text-[color:var(--muted)] hover:text-[color:var(--ink)] transition-colors"
            >
                <span className={`${ACTION_TEXT_CLASS} text-[color:var(--muted)]`}>
                    {label}{" "}
                    <span
                        className="tabular-nums inline-block text-right"
                        style={{ minWidth: `${countMinCh}ch` }}
                    >
                        ({visibleCount}/{columns.length})
                    </span>
                </span>
            </button>
            {/* Portaled to <body>: the popover is position:fixed, and a
                transformed/will-change ancestor (SplitPage's FLIP column
                machinery in Explore) would otherwise turn "fixed" into
                "absolute within the transform" — clipped by the trigger
                row and effectively invisible. The body never has one. */}
            {open ? createPortal(
                <div
                    ref={popRef}
                    role="menu"
                    // Fixed width — relying on `min-w / max-w` lets
                    // the popover resize when the active footer text
                    // ("Showing default columns" vs "Custom selection
                    // saved") changes intrinsic content width. Pin
                    // it to one stable size so the anchor doesn't
                    // appear to "shrink" when toggling columns.
                    className="fixed z-[9999] grid max-h-[60vh] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden border border-[color:var(--line-strong)] bg-[color:var(--bg)] shadow-lg"
                    style={{ top: `${pos.top}px`, right: `${pos.right}px`, width: "320px" }}
                >
                    <div className="flex items-center justify-between gap-[8px] border-b border-[color:var(--line)] px-[10px] py-[8px]">
                        <span className={`${ACTION_TEXT_CLASS} text-[color:var(--muted)]`}>
                            {label}
                        </span>
                        <button
                            type="button"
                            onClick={handleShowAll}
                            disabled={allShown}
                            className={[
                                "border-0 bg-transparent p-0 text-[10px] uppercase tracking-[0.08em] transition-colors",
                                allShown
                                    ? "cursor-not-allowed text-[color:var(--muted-soft)]"
                                    : "cursor-pointer text-[color:var(--muted)] hover:text-[color:var(--ink)]",
                            ].join(" ")}
                        >
                            Show all
                        </button>
                    </div>
                    <div className="overflow-y-auto p-[6px]">
                        {/* Chip-style toggle list — one button per column,
                            visual treatment matches TraceLogPanel's
                            context-control chips (Same Session / Same
                            Endpoint / …). */}
                        <div className="flex flex-col gap-[3px]">
                            {columns.map((c) => {
                                const checked = visibleIds.has(c.id);
                                return (
                                    <button
                                        key={c.id}
                                        type="button"
                                        role="menuitemcheckbox"
                                        aria-checked={checked}
                                        onClick={() => onToggle(c.id)}
                                        className={[
                                            "flex w-full cursor-pointer items-center justify-between gap-[6px] rounded-[2px] border px-[8px] py-[5px] text-left text-[11px] leading-[1.2] transition-colors",
                                            checked
                                                ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)] font-medium"
                                                : "border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)]",
                                        ].join(" ")}
                                    >
                                        <span className="truncate">{c.label}</span>
                                        {/* Tiny indicator on the right edge —
                                            no native checkbox glyph. The
                                            green dot mirrors the active-tab
                                            dot used elsewhere in the shell. */}
                                        <span
                                            aria-hidden="true"
                                            className={[
                                                "h-[6px] w-[6px] flex-none rounded-[999px] transition-colors",
                                                checked
                                                    ? "bg-[color:var(--green)]"
                                                    : "bg-[color:var(--line)]",
                                            ].join(" ")}
                                        />
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    {onResetToDefault ? (
                        <div className="flex items-center gap-[8px] border-t border-[color:var(--line)] px-[10px] py-[8px]">
                            <button
                                type="button"
                                onClick={onResetToDefault}
                                disabled={!isOverridden}
                                className={[
                                    "border-0 bg-transparent p-0 text-[10px] uppercase tracking-[0.08em] transition-colors",
                                    isOverridden
                                        ? "cursor-pointer text-[color:var(--muted)] hover:text-[color:var(--ink)]"
                                        : "cursor-not-allowed text-[color:var(--muted-soft)]",
                                ].join(" ")}
                            >
                                Reset to default
                            </button>
                            <span className={`${NOTE_CLASS} ml-auto text-[10px] text-[color:var(--muted-soft)]`}>
                                {isOverridden ? "Custom selection saved" : "Showing default columns"}
                            </span>
                        </div>
                    ) : null}
                </div>,
                document.body,
            ) : null}
        </div>
    );
}
